import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchBlogPosts,
  formatBlogDate,
  isSanityConfigured,
} from "../lib/sanity";

const PageFooter = () => (
  <div id="footer">
    <div className="container text-center">
      <p>
        &copy; 2026 ByTheApp. All rights reserved.{" "}
        <a href="mailto:info@bytheapp.com">info@bytheapp.com</a>
        {" · "}
        <Link to="/blog">Blog</Link>
        {" · "}
        <Link to="/integrations">Integrations</Link>
        {" · "}
        <Link to="/privacy">Privacy Policy</Link>
        {" · "}
        <Link to="/terms">Terms</Link>
      </p>
    </div>
  </div>
);

const BlogNav = () => (
  <nav className="navbar navbar-default navbar-fixed-top">
    <div className="container">
      <div className="navbar-header">
        <button
          type="button"
          className="navbar-toggle collapsed"
          data-toggle="collapse"
          data-target="#blog-nav-collapse"
        >
          <span className="sr-only">Toggle navigation</span>
          <span className="icon-bar" />
          <span className="icon-bar" />
          <span className="icon-bar" />
        </button>
        <Link className="navbar-brand" to="/">
          By The App
        </Link>
      </div>
      <div className="collapse navbar-collapse" id="blog-nav-collapse">
        <ul className="nav navbar-nav navbar-right">
          <li>
            <Link to="/#products">Products</Link>
          </li>
          <li>
            <Link to="/#about">About</Link>
          </li>
          <li>
            <Link to="/integrations">Integrations</Link>
          </li>
          <li>
            <Link to="/blog">Blog</Link>
          </li>
          <li>
            <Link to="/#pricing">Pricing</Link>
          </li>
          <li>
            <Link to="/#contact">Contact</Link>
          </li>
        </ul>
      </div>
    </div>
  </nav>
);

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!isSanityConfigured()) {
        setLoading(false);
        setError("cms-not-configured");
        return;
      }

      try {
        const data = await fetchBlogPosts();
        if (!cancelled) {
          setPosts(Array.isArray(data) ? data : []);
          setError("");
        }
      } catch (err) {
        console.error("Failed to load blog posts:", err);
        if (!cancelled) {
          setError("Failed to load posts. Please try again later.");
          setPosts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="blog-page">
      <BlogNav />

      <section className="blog-hero">
        <div className="container">
          <span className="blog-eyebrow">Resources</span>
          <h1 className="blog-title">
            News and perspectives on AI voice and business automation
          </h1>
          <p className="blog-lead">
            Product updates, customer workflows, and our take on how intelligent
            agents are reshaping everyday business operations.
          </p>
        </div>
      </section>

      <section className="blog-list-section">
        <div className="container">
          <h2 className="blog-list-heading">Explore our growing collection</h2>

          {loading && <p className="blog-status">Loading posts…</p>}

          {!loading && error === "cms-not-configured" && (
            <div className="blog-empty">
              <p>
                Blog CMS is not connected yet. Add your Sanity project ID to
                enable weekly publishing.
              </p>
              <p className="blog-empty-hint">
                Set <code>REACT_APP_SANITY_PROJECT_ID</code> (and optional{" "}
                <code>REACT_APP_SANITY_DATASET</code>) in your env, then publish
                posts from Sanity Studio. See <code>BLOG_CMS_SETUP.md</code>.
              </p>
            </div>
          )}

          {!loading && error && error !== "cms-not-configured" && (
            <p className="blog-status blog-status-error">{error}</p>
          )}

          {!loading && !error && posts.length === 0 && (
            <div className="blog-empty">
              <p>No posts published yet. Check back soon.</p>
            </div>
          )}

          {!loading && posts.length > 0 && (
            <div className="blog-list">
              {posts.map((post) => (
                <article key={post._id} className="blog-card">
                  <div className="blog-card-meta">
                    <time dateTime={post.publishedAt}>
                      {formatBlogDate(post.publishedAt)}
                    </time>
                    {post.readMinutes ? (
                      <span>{post.readMinutes} MIN</span>
                    ) : null}
                  </div>
                  <h3 className="blog-card-title">
                    <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                  </h3>
                  {post.excerpt && (
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                  )}
                  <Link to={`/blog/${post.slug}`} className="blog-card-read">
                    Read
                  </Link>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="blog-cta">
        <div className="container">
          <h2>Ready to automate customer conversations?</h2>
          <p>See how ByTheApp AI voice agents fit your business.</p>
          <Link to="/#contact" className="btn btn-custom btn-lg">
            Request a Demo
          </Link>
        </div>
      </section>

      <PageFooter />
    </div>
  );
};

export default Blog;
